<script>
import { toast } from "@zerodevx/svelte-toast";
import { projectState } from "@app/store/projectStore.svelte.js";
import { editorState, filterState, resetFilters, hasActiveFilters } from "@app/store/editorStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
import { speakerState } from "@app/store/speakerStore.svelte.js";
import { styleState } from "@app/store/styleStore.svelte.js";
import * as m from '@app/paraglide/messages.js';
import { VList } from "virtua/svelte";
import EventGroupNavbar from "./EventGroupNavbar.svelte";
import FilterBar from "./FilterBar.svelte";
import ProjectInfo from "./ProjectInfo.svelte";
import EventsTable from "./EventsTable.svelte";
import ContextMenu from "./contextMenu/ContextMenu.svelte";
// Import all event components
import SubtitleEvent from "./events/SubtitleEvent.svelte";
import TranscriptEvent from "./events/TranscriptEvent.svelte";
import TranslationEvent from "./events/TranslationEvent.svelte";
import AudioDescriptionEvent from "./events/AudioDescriptionEvent.svelte";
import MultiviewEvent from "./events/MultiviewEvent.svelte";
import { onMount, tick } from "svelte";
import {
    hasEventErrors,
    hasMissingTimecode,
    eventContainsText,
    eventHasSpeaker,
    eventHasTag,
    eventMatchesLineCount,
    eventMatchesForced,
    eventMatchesApproval,
    eventHasIncompleteComment,
    eventNeedsRender,
    eventMatchesTrack,
} from "@app/lib/eventValidation.js";
import * as mammoth from "mammoth/mammoth.browser";
import * as ExcelJS from "exceljs";
import debounce from "just-debounce-it";

/* CC LIB */
import decode from "@cclib/functions/decode.js";
import defaults from "@cclib/lib/defaults.js";
import formats from "@cclib/lib/formats.js";
import _EventGroup from "@cclib/classes/eventGroup.js";
import automaticOs from "@cclib/functions/special/automaticOffset.js";
import tcLib from "@cclib/lib/timecode.js";
import incodeTime from "@cclib/lib/incodeTime.js";
import getAutomaticOffsetAmount from "@cclib/functions/special/getAutomaticOffsetAmount.js";
import bufferToHex from "@cclib/functions/utility/bufferToHex.js";
import fixOverlap from "@cclib/functions/eventGroups/fixOverlap.js";
import stripTags from "@cclib/functions/quill/stripTags.js";
import encodeHtml from "@cclib/functions/eventGroups/encodeHtml.js";
import checkForOverlaps from "@cclib/functions/eventGroups/checkForOverlaps.js";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import { isAudioGroupType } from "@app/lib/eventGroupTypes.js";

/** @type {{editorHeight?: number}} */
let showContextMenu = $state(false);
let ContextMenuOptions = $state({
    x: 0,
    y: 0,
});

// Measured from #EventList via bind:clientHeight (available pane height).
let listHeight = $state(0);
// svelte-ignore non_reactive_update
let listRef = false;

// Track previous selected event group to clear filters on change
let previousSelectedGroup = $state(projectState.selected);

// Save the list scroll offset on the event group it belongs to (debounced).
// The group is captured at scroll time so a late flush can't write to a newly selected group.
let scrollSaveTimer;
function handleListScroll(offset) {
    const eventGroup = eventGroupState[projectState.selected];
    if (!eventGroup) return;

    clearTimeout(scrollSaveTimer);
    scrollSaveTimer = setTimeout(() => {
        eventGroup.scrollPosition = offset;
    }, 100);
}

// Restore the saved scroll offset for the currently selected event group
async function restoreScrollPosition() {
    if (editorState.eventsViewMode === "table") return;

    const eventGroup = eventGroupState[projectState.selected];
    if (!eventGroup || !listRef) return;

    // Wait for the VList to render the new group's events before scrolling
    await tick();
    listRef.scrollTo(eventGroup.scrollPosition ?? 0);
}

// Effect to clear filters when switching event groups
$effect(() => {
    if (projectState.selected !== previousSelectedGroup) {
        resetFilters();
        previousSelectedGroup = projectState.selected;
        restoreScrollPosition();
    }
});

// Multiview: track info for column headers and per-event track selectors
const MULTIVIEW_HEADER_HEIGHT = 28;
let multiviewTracks = $derived.by(() => {
    const eventGroup = eventGroupState[projectState.selected];
    if (!eventGroup || eventGroup.type !== "multiview") return [];

    if (Array.isArray(eventGroup.tracks) && eventGroup.tracks.length >= 2) {
        return eventGroup.tracks;
    }

    // Fallback for multiview groups without track info: derive count from events
    const maxTrack = Math.max(
        2,
        ...eventGroup.events.map((event) => event.track || 1),
    );
    return Array.from({ length: maxTrack }, (_, i) => ({
        name: `${m.multiview_track()} ${i + 1}`,
    }));
});

// Selected group type for the list template. The selected group can briefly be
// missing (e.g. while a new project resets the event groups), so read it safely.
let selectedGroupType = $derived(eventGroupState[projectState.selected]?.type);

// Letter track columns (A-D) for the selected non-multiview group.
// Runs from A up to the highest letter in use (minimum two columns) so each
// letter keeps a fixed column and a single assigned track is still visible.
// Drives the multi-column layout of the list view.
const TRACK_LETTERS = ["A", "B", "C", "D"];
let letterTracks = $derived.by(() => {
    const eventGroup = eventGroupState[projectState.selected];
    if (!eventGroup || eventGroup.type === "multiview") return [];

    let highest = -1;
    eventGroup.events.forEach((event) => {
        highest = Math.max(highest, TRACK_LETTERS.indexOf(event.track));
    });

    if (highest === -1) return [];
    return TRACK_LETTERS.slice(0, Math.max(2, highest + 1));
});

/* Grid column for an event in the track layout. Untracked events span the full row. */
function trackGridColumn(event) {
    const position = letterTracks.indexOf(event?.track);
    return position === -1 ? "1 / -1" : `${position + 1}`;
}

// Filtered events with original index mapping
let filteredEvents = $derived.by(() => {
    if (!eventGroupState[projectState.selected]) return [];
    
    const events = eventGroupState[projectState.selected].events;
    const eventGroup = eventGroupState[projectState.selected];
    
    // If no filters are active, return all events with their indices
    if (!hasActiveFilters()) {
        return events.map((event, index) => ({ event, originalIndex: index }));
    }
    
    // Apply all filters
    return events
        .map((event, index) => ({ event, originalIndex: index }))
        .filter(({ event, originalIndex }) => {
            // Text search filter
            if (filterState.text && !eventContainsText(event, filterState.text)) {
                return false;
            }
            
            // Error filter
            if (filterState.hasError && !hasEventErrors(event, eventGroup, originalIndex)) {
                return false;
            }
            
            // Missing timecode filter
            if (filterState.hasMissingTimecode && !hasMissingTimecode(event)) {
                return false;
            }
            
            // Line count filter
            if (!eventMatchesLineCount(event, filterState.lineCounts)) {
                return false;
            }
            
            // Forced events filter (forced / non-forced, OR within group)
            if (!eventMatchesForced(event, { forced: filterState.hasForced, nonForced: filterState.hasNonForced })) {
                return false;
            }
            
            // Approval filter (failed / approved / needs review, OR within group)
            if (!eventMatchesApproval(event, { failed: filterState.hasFailedApproval, approved: filterState.hasApproved, needsReview: filterState.hasNeedsReview })) {
                return false;
            }
            
            // Incomplete comment filter
            if (filterState.hasIncompleteComment && !eventHasIncompleteComment(event)) {
                return false;
            }

            // Render required filter (AD / dubbing audio out of date)
            if (filterState.hasRenderRequired && !eventNeedsRender(event)) {
                return false;
            }
            
            // Speaker filter
            if (!eventHasSpeaker(event, filterState.speakers)) {
                return false;
            }
            
            // Tag filter
            if (!eventHasTag(event, filterState.tags)) {
                return false;
            }

            // Track filter (letter A-D / none, or numeric multiview; OR within group)
            if (!eventMatchesTrack(event, filterState.tracks)) {
                return false;
            }
            
            return true;
        });
});

// Count for filter status display
let totalEventCount = $derived(eventGroupState[projectState.selected]?.events?.length || 0);
let filteredEventCount = $derived(filteredEvents.length);

onMount(() => {
    // Restore the saved scroll position for the initially selected group
    restoreScrollPosition();

    // Listen for custom scroll events with improved clarity and reliability
    const handleScrollToEvent = debounce((event) => {
        // Table view handles scroll via EventsTable listener
        if (editorState.eventsViewMode === "table") return;

        const originalIndex = parseInt(event.detail.index);
        // Check if index is valid
        if (isNaN(originalIndex) || originalIndex < 0 || originalIndex >= eventGroupState[projectState.selected].events.length) return;
        
        // Find the index in the filtered list that corresponds to this original index
        const filteredIndex = filteredEvents.findIndex(item => item.originalIndex === originalIndex);
        
        // If the event is visible in the filtered list, scroll to it
        if (filteredIndex !== -1 && listRef) {
            listRef.scrollToIndex(filteredIndex, {
                align: "nearest",
                smooth: false
            });
        }
    }, 50);

    /* Scroll Event */
    window.addEventListener("scrollToVirtualListItem", handleScrollToEvent);

    return () => {
        window.removeEventListener(
            "scrollToVirtualListItem",
            handleScrollToEvent,
        );
        clearTimeout(scrollSaveTimer);
    };
});

async function openContextMenu(e) {
    // Let SVAR HeaderMenu handle column header right-clicks (it preventDefaults
    // but does not stopPropagation, so this handler would otherwise steal the menu).
    if (e.defaultPrevented) return;
    if (
        e.target?.closest?.(
            ".events-table-wrap .wx-header, .events-table-wrap [data-header-id], [data-header-id]",
        )
    ) {
        return;
    }

    e.preventDefault();
    let eventId;
    let path = e.composedPath();
    [5, 4, 3, 6, 7, 8].forEach((pathDepth) => {
        if (path[pathDepth] && /Event\d+/.test(path[pathDepth].id)) {
            eventId = parseInt(path[pathDepth].id.split("Event")[1]);
            if (
                eventGroupState[projectState.selected].selected.indexOf(
                    eventId,
                ) === -1
            ) {
                eventGroupState[projectState.selected].selected = [eventId];
            }
        } else {
            return;
        }
    });

    ContextMenuOptions = {
        x: e.clientX,
        y: Math.max(150, e.clientY - 580),
    };

    showContextMenu = true;
}

function readSubtitleFile(ev) {
    ev.preventDefault();
    const items = Object.values(ev.dataTransfer.items);
    if (items.length <= 0) {
        return; // Achievement get: How did we get here?
    } else if (items.length == 1) {
        readFile(items[0]);
    } else {
        items.forEach((item) => {
            readFile(item);
        });
    }
}

async function readFile(item) {
    try {
        if (item && item.kind === "file") {
            let subtitleFile = item.getAsFile();
            let fileName = subtitleFile.name.split(".");
            let srcFileName = fileName[0];
            let srcExt = fileName.pop().toLowerCase();
            let possibleProfiles = formats.srcProfiles[srcExt];

            if (!possibleProfiles || possibleProfiles.length === 0) {
                toast.push(
                    m.editor_unsupported_file_type_toast(),
                    { classes: ["toast-danger"] },
                );
                return;
            }

            let srcProfile = formats.srcProfiles[srcExt][0];

            //Ask the user to select a profile based on source file extension. If possible profiles is empty, use the first profile in the list.
            if (possibleProfiles.length > 1) {
                let profileRes = await Swal.fire({
                    ...SWAL_BASE,
                    customClass: SWAL_CLASSES.ok,
                    title: m.editor_select_profile_title(),
                    text: m.editor_select_profile_text({fileType: srcExt.toUpperCase()}),
                    input: "select",
                    inputOptions: possibleProfiles.reduce((acc, profile) => {
                        acc[profile] = formats.profileMapping[profile].name;
                        return acc;
                    }, {}),
                    inputPlaceholder: m.editor_select_profile_placeholder(),
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                });

                if (profileRes.isConfirmed) {
                    srcProfile = profileRes.value;
                } else {
                    toast.push(m.editor_import_cancelled(), {
                        classes: ["toast-warning"],
                    });
                    return;
                }
            }

            if (!srcProfile) {
                console.log(srcProfile);
                toast.push(m.editor_import_failed(), { classes: ["toast-danger"] });
                return;
            }

            let fileEncoding = formats.defaultEncodingMap[srcProfile];
            let reader = new FileReader();
            reader.onload = async function (e) {
                if (srcExt.toLowerCase() === "docx") {

                    let result = srcProfile === "MS2 Transcript" ? await mammoth.convertToHtml({ arrayBuffer: e.target.result }) : await mammoth.extractRawText({ arrayBuffer: e.target.result });

					fileContents = result.value;

                    importSubtitles(
                        fileContents,
                        srcProfile,
                        srcFileName,
                    );
                } else if (srcExt.toLowerCase() === "xlsx") {
                    const workbook = new ExcelJS.Workbook();
                    await workbook.xlsx.load(e.target.result);
                    let xlsxContent = JSON.stringify(workbook.model, null, 4);
                    importSubtitles(xlsxContent, srcProfile, srcFileName);
                } else if (
                    formats.binaryFormats.includes(
                        formats.profileMapping[srcProfile].name,
                    )
                ) {
                    importSubtitles(
                        bufferToHex(e.target.result),
                        srcProfile,
                        srcFileName,
                    );
                } else {
                    importSubtitles(e.target.result, srcProfile, srcFileName);
                }
            };

            formats.binaryFormats.includes(
                formats.profileMapping[srcProfile].name,
            ) ||
            srcExt === "docx" ||
            srcExt === "xlsx"
                ? reader.readAsArrayBuffer(subtitleFile)
                : reader.readAsText(subtitleFile, fileEncoding);
        }
    } catch (err) {
        console.log(err, err.message);
    }
}

async function importSubtitles(fileContents, srcProfile, srcFileName) {
    try {
        toast.push(m.editor_import_subtitles_start_toast(), {
            classes: ["toast-warning"],
        });

        let eventGroupDefaults =
            JSON.parse(localStorage.getItem("cc-event-group-defaults")) || {};
        let overlap = eventGroupDefaults.overlap,
            maxCps = eventGroupDefaults.maxCps || 9999,
            maxWpm = eventGroupDefaults.maxWpm || 9999,
            maxChars = eventGroupDefaults.maxChars || 32,
            maxLines = eventGroupDefaults.maxLines || 2,
            minDuration = eventGroupDefaults.minDuration || 0.5,
            maxDuration = eventGroupDefaults.maxDuration || 12;

        let eventGroupOptions = {
            type: "subtitle",
            name: srcFileName,
            language: "en-US",
            rtl: false,
            overlap: overlap,
            maxCps: maxCps || 9999,
            maxWpm: maxWpm || 9999,
            maxChars: maxChars || 9999,
            maxLines: maxLines || 9999,
            minDuration: minDuration || 0.5,
            maxDuration: maxDuration || 12,
            fontFamily: eventGroupDefaults.fontFamily,
            fontSize: eventGroupDefaults.fontSize || 16
        };

        // Push new event group using Svelte 5 reactivity
        eventGroupState.push({ ...new _EventGroup(eventGroupOptions) });
        projectState.selected = eventGroupState.length - 1;
        console.log(
            `Importing subtitle file using ${formats.profileMapping[srcProfile].name} profile`,
        );

        // A Project Incode set by the user takes priority over the automatic offset detected from the file
        let incode = incodeTime.mediaSecondsToIncodeLabel(projectState.incode, projectState.frameRate, projectState.dropFrame);

        console.log("INCODE USED FOR IMPORT: " + incode);

        let options = new defaults.options({
            profile: formats.profileMapping[srcProfile].name,
            formatOptions: formats.profileMapping[srcProfile].options.decode,
            frameRate: projectState.frameRate,
            dropFrame: projectState.dropFrame,
            target_profile: "closedCaptionProject",
            incode: incode,
            source_profile: formats.profileMapping[srcProfile].name,
            window: {
                width: styleState.width,
                height: styleState.height,
                xPadding: styleState.xPadding,
                yPadding: styleState.yPadding
            }
        });

        let decodedEvGroup = await decode(fileContents, options);
        decodedEvGroup = encodeHtml(decodedEvGroup);
        let offsetAmount = projectState.incode > 0 || decodedEvGroup.events.length === 0
            ? 0
            : getAutomaticOffsetAmount(
                decodedEvGroup,
                projectState.frameRate,
                projectState.dropFrame,
                tcLib.secToTc(
                    projectState.incode,
                    projectState.frameRate,
                    projectState.dropFrame
                ),
            );

        console.log("AUTOMATIC OFFSET AMOUNT (in seconds): " + offsetAmount);

        if (offsetAmount != 0) {
            try {
                let offsetRes = await Swal.fire({
                    ...SWAL_BASE,
                    customClass: SWAL_CLASSES.confirmCancelLight,
                    title: m.editor_automatic_offset_title(),
                    text: m.editor_automatic_offset_text({offset: tcLib.secToTcClock(offsetAmount, projectState.frameRate, projectState.dropFrame)}),
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonText: m.editor_yes_button(),
                    cancelButtonText: m.editor_no_button(),
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                });

                if (offsetRes.isConfirmed) {
                    projectState.incode = offsetAmount;
                    decodedEvGroup = automaticOs(
                        decodedEvGroup,
                        projectState.frameRate,
                        projectState.dropFrame
                    );
                }
            } catch (err) {
                console.log(err, err.message);
            }
        }

        if (decodedEvGroup.events.every((ev) => ev.style === "Pop-On" || ev.style === "Paint-On")) {
            decodedEvGroup.events = decodedEvGroup.events.filter((event) => {
                return stripTags(event.text) || event.notes || event.reply;
            });

            //Check for overlaps in the new event group. If overlaps are found, ask the user if they want them fixed using SweetAlert2
            if (checkForOverlaps(decodedEvGroup)) {
                let overlapRes = await Swal.fire({
                    ...SWAL_BASE,
                    customClass: SWAL_CLASSES.confirmCancelLight,
                    title: m.editor_overlapping_events_title(),
                    text: m.editor_overlapping_events_text(),
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonText: m.editor_yes_button(),
                    cancelButtonText: m.editor_no_button(),
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                });

                if (overlapRes.isConfirmed) {
                    decodedEvGroup = fixOverlap(
                        decodedEvGroup,
                        editorState.minFrameGap,
                        projectState.frameRate,
                    );
                }
            }
        }

        //Import Speakers from events
        decodedEvGroup.events.forEach((event) => {
            event.speakers.forEach((speaker) => {
                //Check if the speaker already exists in speakerState, otherwise add it.
                if (!speakerState.find((s) => s.name === speaker.name)) {
                    speakerState.push(speaker);
                }
            });
        });

        eventGroupState[projectState.selected].events = decodedEvGroup.events;

        insertHistoryAction({
            name: "import subtitles", //action name
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });
        toast.push(m.editor_import_subtitles_complete_toast(), { classes: ["toast-success"] });
    } catch (err) {
        console.log(err, err.message);
        toast.push(m.editor_import_subtitles_failed_toast({ message: err.message }), {
            classes: ["toast-danger"],
        });
    }
}
</script>

{#if showContextMenu}
    <ContextMenu {...ContextMenuOptions} bind:showContextMenu></ContextMenu>
{/if}
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
    class="shadow bg-secondary bg-opacity-25 rounded p-0 align-self-start"
    id="EventEditor"
    oncontextmenu={openContextMenu}
    ondrop={readSubtitleFile}
    ondragover={(ev) => {
        ev.preventDefault();
    }}>
    <EventGroupNavbar></EventGroupNavbar>

    <div id="EventList" bind:clientHeight={listHeight}>
        {#if eventGroupState[projectState.selected]}
            {#if editorState.eventsViewMode === "table"}
                <EventsTable {filteredEvents} {listHeight} />
            {:else}
                {#if selectedGroupType === "multiview"}
                    <div class="multiview-track-headers" style="grid-template-columns: repeat({multiviewTracks.length}, minmax(0, 1fr)); height: {MULTIVIEW_HEADER_HEIGHT}px;">
                        {#each multiviewTracks as track, trackIndex (trackIndex)}
                            <div class="multiview-track-header text-truncate px-2" title={track.name}>
                                <span class="badge bg-primary me-1">{trackIndex + 1}</span>{track.name}
                            </div>
                        {/each}
                    </div>
                {:else if letterTracks.length > 0}
                    <div class="multiview-track-headers" style="grid-template-columns: repeat({letterTracks.length}, minmax(0, 1fr)); height: {MULTIVIEW_HEADER_HEIGHT}px;">
                        {#each letterTracks as trackLetter (trackLetter)}
                            <div class="multiview-track-header text-truncate px-2" title="{m.multiview_track()} {trackLetter}">
                                <span class="badge bg-primary me-1">{trackLetter}</span>{m.multiview_track()} {trackLetter}
                            </div>
                        {/each}
                    </div>
                {/if}
                <VList bind:this={listRef} style="height:{listHeight - (selectedGroupType === 'multiview' || letterTracks.length > 0 ? MULTIVIEW_HEADER_HEIGHT : 0)}px;" data={filteredEvents} getKey={(_) => _.event.id} itemSize={selectedGroupType === "multiview" ? 210 : 229} onscroll={handleListScroll}>
                    {#snippet children(item, index)}
                        <div class={index % 2 !== 0 ? 'event-odd' : ''}>
                        {#if selectedGroupType !== "multiview" && letterTracks.length > 0}
                            {@const compact = letterTracks.length > 1 && TRACK_LETTERS.includes(item?.event?.track)}
                            <div class="track-grid-row" style="grid-template-columns: repeat({letterTracks.length}, minmax(0, 1fr));">
                                <div style="grid-column: {trackGridColumn(item?.event)};">
                                    {#if selectedGroupType === "subtitle" && item}
                                        <SubtitleEvent eventIndex={item?.originalIndex} {compact} />
                                    {:else if selectedGroupType === "transcription" && item}
                                        <TranscriptEvent eventIndex={item?.originalIndex} {compact} />
                                    {:else if selectedGroupType === "translation" && item}
                                        <TranslationEvent eventIndex={item?.originalIndex} {compact} />
                                    {:else if isAudioGroupType(selectedGroupType) && item}
                                        <AudioDescriptionEvent eventIndex={item?.originalIndex} {compact} />
                                    {/if}
                                </div>
                            </div>
                        {:else if selectedGroupType === "subtitle" && item}
                            <SubtitleEvent eventIndex={item?.originalIndex} />
                        {:else if selectedGroupType === "transcription" && item}
                            <TranscriptEvent eventIndex={item?.originalIndex} />
                        {:else if selectedGroupType === "translation" && item}
                            <TranslationEvent eventIndex={item?.originalIndex} />
                        {:else if isAudioGroupType(selectedGroupType) && item}
                            <AudioDescriptionEvent eventIndex={item?.originalIndex} />
                        {:else if selectedGroupType === "multiview" && item}
                            <MultiviewEvent eventIndex={item?.originalIndex} trackCount={multiviewTracks.length} tracks={multiviewTracks} />
                        {/if}
                        </div>
                    {/snippet}
                </VList>
            {/if}
        {:else}
            <p class="lead text-warning text-center m-5">
                {m.editor_select_event_group_prompt()}
            </p>
        {/if}
    </div>

    <!-- Filter Panel (slides up from bottom) -->
    <FilterBar />

    <div class="row">
        <div class="col">
            {#if eventGroupState[projectState.selected]}
                <ProjectInfo {filteredEventCount} {totalEventCount} />
            {/if}
        </div>
    </div>
</div>

<style lang="css">
#EventList {
    overflow: hidden;
    will-change: transform;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

#EventEditor {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.multiview-track-headers {
    display: grid;
    align-items: center;
    flex-shrink: 0;
    font-weight: bold;
    font-size: 0.85rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.25);
}

.track-grid-row {
    display: grid;
}
</style>
